import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',
  label,
  required = false,
  disabled = false,
  className = '',
  optionLabel = 'title',
  optionValue = 'id',
  multiple = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        const label = option[optionLabel] || '';
        return label.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, optionLabel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle option selection
  const handleOptionSelect = (option) => {
    if (multiple) {
      const current = Array.isArray(value) ? value : [];
      const exists = current.find(v => (v?.[optionValue] ?? v) === option[optionValue]);
      const next = exists
        ? current.filter(v => (v?.[optionValue] ?? v) !== option[optionValue])
        : [...current, option];
      onChange(next);
    } else {
      onChange(option);
      setIsOpen(false);
    }
    setSearchTerm('');
  };

  // Get selected option label
  const getSelectedLabel = () => {
    if (!value || (multiple && (!Array.isArray(value) || value.length === 0))) return '';
    if (multiple) {
      return (value || []).map(v => v[optionLabel]).join(', ');
    }
    return value[optionLabel] || '';
  };

  // Clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    onChange(multiple ? [] : '');
    setSearchTerm('');
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <button
          type="button"
          onClick={handleInputFocus}
          disabled={disabled}
          className={`
            relative w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-lg shadow-sm cursor-pointer
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : 'hover:border-gray-400'}
          `}
        >
          <span className={`block truncate ${!value ? 'text-gray-500' : 'text-gray-900'}`}>
            {getSelectedLabel() || placeholder}
          </span>
          
          <span className="absolute inset-y-0 right-0 flex items-center pr-2">
            {value && (
              <div
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 mr-1 cursor-pointer"
                title="Clear selection"
              >
                <XMarkIcon className="h-4 w-4" />
              </div>
            )}
            <ChevronDownIcon 
              className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                isOpen ? 'transform rotate-180' : ''
              }`} 
            />
          </span>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-hidden"
            style={{ minHeight: '120px' }}
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-48 overflow-y-auto min-h-[80px]">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  No options found
                </div>
              ) : (
                <>
                  <div className="px-3 py-1 text-xs text-gray-400 border-b border-gray-100">
                    {filteredOptions.length} option(s) available
                  </div>
              {filteredOptions.map((option, index) => {
                    const isSelected = multiple
                      ? (Array.isArray(value) && value.some(v => (v?.[optionValue] ?? v) === option[optionValue]))
                      : option[optionValue] === value;
                    const label = option[optionLabel];
                    
                    return (
                      <button
                        key={option[optionValue] || index}
                        onClick={() => handleOptionSelect(option)}
                        className={`
                          w-full px-3 py-2 text-left text-sm cursor-pointer transition-colors
                          ${isSelected 
                            ? 'bg-blue-50 text-blue-700' 
                            : 'text-gray-900 hover:bg-gray-50'
                          }
                        `}
                      >
                        <span className="block truncate">{label}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableSelect;
